import React, { useMemo, useState } from 'react';
import DepartmentDetailDrawer from './DepartmentDetailDrawer';
// Reuses the .admin-table styling shared with Categories/Activity Logs and the
// .ut-drawer/.ut-tablewrap rules from the users-tab module.
import '../usersTab/usersTab.css';

/**
 * Departments tab — table-of-departments + slide-in drawer for the selected
 * row. Mirrors the AdminUsersTab pattern so admins navigate both lists the
 * same way.
 *
 * Props:
 *   departments         list of departments (with optional manager array,
 *                       member_count, tool counts from /api/admin/departments)
 *   selectedDepartment  current selection (owned by AdminDashboard for now)
 *   onSelect            (dept | null)
 *   onAddDepartment     ()
 *   children            JSX rendered inside the drawer body
 */
export default function AdminDepartmentsTab({
  departments,
  selectedDepartment,
  onSelect,
  onAddDepartment,
  children,
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter((d) =>
      d.name?.toLowerCase().includes(q) ||
      (d.description || '').toLowerCase().includes(q)
    );
  }, [departments, search]);

  return (
    <div className="ut-shell" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, height: '100%', overflow: 'hidden' }}>
        <div className="ut-listbar" style={{ flexShrink: 0 }}>
          <input
            type="search"
            placeholder="Search departments by name or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search departments"
          />
          <button type="button" className="btn btn-primary" onClick={onAddDepartment}>
            Add department
          </button>
        </div>

        <div className="admin-table-wrap ut-tablewrap">
          <table className="admin-table ut-table" aria-label="Departments">
            <thead>
              <tr>
                <th>Department</th>
                <th style={{ width: '110px' }}>Members</th>
                <th style={{ width: '140px' }}>Tools</th>
                <th style={{ width: '180px' }}>Odoo department</th>
                <th>Managers</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="5" className="at-empty">
                  {departments.length === 0 ? 'No departments yet.' : 'No departments match your search.'}
                </td></tr>
              )}
              {filtered.map((d) => {
                const isSelected = d.id === selectedDepartment?.id;
                const managers = d.managers || [];
                return (
                  <tr
                    key={d.id}
                    className={`ut-tr ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => onSelect(d)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(d);
                      }
                    }}
                  >
                    <td className="at-primary">
                      <div className="ut-cell-name">{d.name}</div>
                      {d.description && (
                        <div className="ut-cell-email">{d.description}</div>
                      )}
                    </td>
                    <td>{d.member_count ?? '—'}</td>
                    <td>
                      {d.catalog_count != null ? (
                        <span>
                          {d.catalog_count} in catalog
                          {d.default_count != null && (
                            <small style={{ display: 'block', fontSize: 11, color: '#718096' }}>
                              {d.default_count} default{d.default_count === 1 ? '' : 's'}
                              {d.managers_only_count > 0 && ` · ${d.managers_only_count} managers-only`}
                            </small>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      {(d.odoo_department_names && d.odoo_department_names.length > 0)
                        ? <span title={d.odoo_department_names.join(', ')}>{d.odoo_department_names.join(', ')}</span>
                        : <span className="ut-muted">— not mapped —</span>}
                    </td>
                    <td>
                      {managers.length === 0
                        ? <span className="ut-muted">— none —</span>
                        : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {managers.slice(0, 3).map((m) => (
                              <span
                                key={m.id}
                                className="at-pill"
                                style={{ background: '#DBEAFE', color: '#1E40AF' }}
                              >
                                {m.name}
                              </span>
                            ))}
                            {managers.length > 3 && (
                              <span className="at-pill" style={{ background: '#E2E8F0', color: '#4A5568' }}>
                                +{managers.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <DepartmentDetailDrawer
        department={selectedDepartment}
        onClose={() => onSelect(null)}
      >
        {children}
      </DepartmentDetailDrawer>
    </div>
  );
}
